"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    mediaShadowCss,
    scaledPx,
} from "@/features/lomdaEditorV2/components/scene/lib/utils";
import type { MediaElement } from "@/features/lomdaEditorV2/lib/elements/types";

/**
 * Renders a media element inside its canvas box.
 *
 * Rounded corners must hug the visible image, not the element box. In "cover"
 * the image fills the box, so the rounded+clipped box is the box itself. In
 * "contain" the image letterboxes, so we shrink an inner box to the image's
 * intrinsic aspect ratio and round/clip THAT — otherwise the corners would
 * round the empty letterbox area and look un-rounded against the image.
 *
 * The inner size lives in React state (not imperative inline styles) so it
 * survives the re-renders triggered by every media edit.
 */
export function MediaElementView({ element }: { element: MediaElement }) {
    const { media } = element;
    const objectFit = media.objectFit;
    const borderRadius = scaledPx(media.borderRadius);
    const boxShadow = mediaShadowCss(media.shadow);

    const outerRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [aspect, setAspect] = useState<number | null>(null);
    const [innerSize, setInnerSize] = useState<{ w: string; h: string }>({
        w: "100%",
        h: "100%",
    });

    const useFittedInner = objectFit === "contain";

    // New source → forget the previous aspect so we don't fit to it.
    useEffect(() => {
        setAspect(null);
    }, [media.url]);

    // Pick up intrinsic size from an already-loaded (cached) image/video where
    // onLoad/onLoadedMetadata may not fire.
    useEffect(() => {
        const img = imgRef.current;
        if (img && img.complete && img.naturalWidth && img.naturalHeight) {
            setAspect(img.naturalWidth / img.naturalHeight);
            return;
        }
        const video = videoRef.current;
        if (video && video.readyState >= 1 && video.videoWidth) {
            setAspect(video.videoWidth / video.videoHeight);
        }
    }, [media.url]);

    useLayoutEffect(() => {
        const outer = outerRef.current;
        if (!outer) return;
        if (!useFittedInner || aspect == null) {
            setInnerSize((s) =>
                s.w === "100%" && s.h === "100%" ? s : { w: "100%", h: "100%" }
            );
            return;
        }
        const fit = () => {
            const r = outer.getBoundingClientRect();
            if (!r.width || !r.height) return;
            const boxAspect = r.width / r.height;
            let w: number;
            let h: number;
            if (aspect > boxAspect) {
                w = r.width;
                h = r.width / aspect;
            } else {
                h = r.height;
                w = r.height * aspect;
            }
            const next = { w: `${Math.round(w)}px`, h: `${Math.round(h)}px` };
            setInnerSize((s) => (s.w === next.w && s.h === next.h ? s : next));
        };
        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(outer);
        return () => observer.disconnect();
    }, [useFittedInner, aspect]);

    return (
        <div
            ref={outerRef}
            className="flex h-full w-full items-center justify-center"
        >
            <div
                className="relative overflow-hidden"
                style={{
                    width: innerSize.w,
                    height: innerSize.h,
                    borderRadius,
                    boxShadow,
                }}
            >
                {media.type === "video" ? (
                    <video
                        ref={videoRef}
                        src={media.url}
                        data-render-layer="2"
                        muted
                        loop
                        playsInline
                        className="pointer-events-none h-full w-full select-none"
                        style={{ objectFit }}
                        onLoadedMetadata={(e) => {
                            const v = e.currentTarget;
                            if (v.videoWidth && v.videoHeight)
                                setAspect(v.videoWidth / v.videoHeight);
                        }}
                    />
                ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        ref={imgRef}
                        src={media.url}
                        alt=""
                        draggable={false}
                        className="pointer-events-none h-full w-full select-none"
                        style={{ objectFit }}
                        onLoad={(e) => {
                            const i = e.currentTarget;
                            if (i.naturalWidth && i.naturalHeight)
                                setAspect(i.naturalWidth / i.naturalHeight);
                        }}
                    />
                )}
            </div>
        </div>
    );
}
